"use client"

import { useEffect, useState } from "react"
import { Command, Input, Popover, PopoverContent, PopoverTrigger } from "@ui/shad"
import { ChevronLeft, ChevronRight, Cloud, FileText } from "lucide-react"

import { useStorageIntegrations, useStoragePickerSearch } from "@/hooks/api/storage_integrations"
import { useStoragePickerPages } from "@/hooks/api/storage/pages"
import { StoragePageList } from "@/components/storage/storage-page-list"

const SEARCH_DEBOUNCE_MS = 300

interface DriveAttachmentButtonProps {
  onAttach: (link: { url: string; name: string }) => void
  // Provider folder id connected to the record this note belongs to. The picker opens
  // here instead of the drive root so the relevant files are the first thing shown.
  // When the record has no linked folder, the picker browses the drive root.
  rootFolderId?: string
  // Display name for rootFolderId. Ignored when rootFolderId is absent.
  rootFolderName?: string
}

export function DriveAttachmentButton(props: DriveAttachmentButtonProps) {
  return <DriveAttachmentContent key={props.rootFolderId ?? "root"} {...props} />
}

function DriveAttachmentContent({ onAttach, rootFolderId, rootFolderName }: DriveAttachmentButtonProps) {
  const [selectedId, setSelectedId] = useState<string>()
  const [open, setOpen] = useState(false)
  const [folderStack, setFolderStack] = useState<{ id: string; name: string }[]>([])
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const currentFolder = folderStack[folderStack.length - 1]
  const currentFolderId = currentFolder?.id ?? rootFolderId

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timeout)
  }, [search])

  const { data: integrations } = useStorageIntegrations()
  const integration = integrations?.find((i) => i.is_active && i.auth_status === "valid")

  const isShareFile = integration?.type === "StorageIntegration::ShareFile"
  const canSearchOnServer = !!integration?.supports_picker_search
  const isSearching = canSearchOnServer && debouncedSearch.length > 0
  const browsePage = useStoragePickerPages({
    integrationId: integration?.id,
    folderId: currentFolderId,
    rootFolderId,
    enabled: open && !!integration?.id && !isSearching,
    nameContains: isShareFile ? debouncedSearch : undefined,
  })

  const {
    data: searchData,
    isLoading: isSearchLoading,
    error: searchError,
    refetch: retrySearch,
    errorUpdatedAt: searchErrorAt,
  } = useStoragePickerSearch(integration?.id ?? "", debouncedSearch, {
    enabled: open && !!integration?.id && canSearchOnServer,
  })

  const items = ((isSearching ? searchData?.files : browsePage.files) ?? [])
    .filter((file) => canSearchOnServer || isShareFile || file.name.toLowerCase().includes(search.toLowerCase()))
    .map(({ id, name, type, web_url, mime_type }) => ({ id, name, type, web_url, mime_type }))
  const selected =
    items.find((file) => file.id === selectedId) ?? items.find((file) => file.type === "folder" || file.web_url)
  const page = isSearching
    ? {
        ...browsePage,
        files: items,
        isLoading: isSearchLoading,
        error: searchError,
        errorUpdatedAt: searchErrorAt,
        retry: () => void retrySearch(),
        hasNextPage: false,
      }
    : { ...browsePage, files: items }
  // Only name the location when a folder is actually linked. Records without one browse
  // the drive root, and labeling that with the record's name would imply a folder that
  // doesn't exist.
  const rootLabel = rootFolderId ? (rootFolderName ?? "Linked folder") : "My Drive"
  const currentLocation = currentFolder?.name ?? rootLabel

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) {
      setFolderStack([])
      setSearch("")
      setDebouncedSearch("")
    }
  }

  const openFolder = (folder: { id: string; name: string }) => {
    setFolderStack((s) => [...s, folder])
    setSearch("")
    setDebouncedSearch("")
  }

  const goBack = () => {
    setFolderStack((s) => s.slice(0, -1))
    setSearch("")
    setDebouncedSearch("")
  }

  const handleSelectFile = (file: { web_url?: string; name: string }) => {
    if (!file.web_url) return
    onAttach({ url: file.web_url, name: file.name })
    handleOpenChange(false)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-primary flex h-8 cursor-pointer items-center gap-1.5 rounded-md px-2 text-sm focus:outline-1"
        >
          <Cloud className="size-3.5" />
          <span>Attach from drive</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0">
        {!integration ? (
          <div className="text-muted-foreground p-4 text-sm">
            No connected drive. Connect one in settings to attach files.
          </div>
        ) : (
          <Command shouldFilter={false}>
            <Input
              placeholder="Search files..."
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
                setSelectedId(undefined)
              }}
              role="combobox"
              aria-expanded
              aria-controls="drive-attachment-options"
              aria-activedescendant={selected ? `drive-option-${selected.id}` : undefined}
              onKeyDown={(event) => {
                if (!["ArrowDown", "ArrowUp", "Home", "End", "Enter"].includes(event.key)) return
                event.preventDefault()
                event.stopPropagation()
                if (event.key === "Enter") {
                  if (selected) {
                    if (selected.type === "folder") openFolder(selected)
                    else handleSelectFile(selected)
                  }
                  return
                }
                const selectable = items.filter((file) => file.type === "folder" || file.web_url)
                const index = selectable.findIndex((file) => file.id === selected?.id)
                const next =
                  event.key === "Home"
                    ? 0
                    : event.key === "End"
                      ? selectable.length - 1
                      : event.key === "ArrowUp"
                        ? Math.max(0, index - 1)
                        : Math.min(selectable.length - 1, index + 1)
                setSelectedId(selectable[next]?.id)
              }}
            />
            {isSearching ? (
              <div className="text-muted-foreground border-b px-3 py-2 text-xs">Searching all files</div>
            ) : folderStack.length > 0 ? (
              <button
                type="button"
                onClick={goBack}
                className="hover:bg-muted flex items-center gap-1 border-b px-3 py-2 text-left text-sm"
              >
                <ChevronLeft className="size-4" />
                <span className="truncate">{currentLocation}</span>
              </button>
            ) : (
              <div className="text-muted-foreground truncate border-b px-3 py-2 text-xs">{currentLocation}</div>
            )}
            <div id="drive-attachment-options" role="listbox">
              <StoragePageList
                page={page}
                height={280}
                scrollToKey={selected?.id}
                pinnedKeys={selected ? [selected.id] : []}
              >
                {(file) => (
                  <button
                    id={`drive-option-${file.id}`}
                    role="option"
                    aria-selected={selected?.id === file.id}
                    className={`flex h-10 w-full items-center gap-2 px-3 text-left text-sm ${selected?.id === file.id ? "bg-muted" : "hover:bg-muted/50"}`}
                    disabled={file.type !== "folder" && !file.web_url}
                    onClick={() => (file.type === "folder" ? openFolder(file) : handleSelectFile(file))}
                  >
                    {file.type !== "folder" && <FileText className="text-icon size-4 shrink-0" />}
                    <span className="min-w-0 flex-1 truncate">{file.name}</span>
                    {file.type === "folder" && <ChevronRight className="text-icon size-4 shrink-0" />}
                  </button>
                )}
              </StoragePageList>
            </div>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  )
}
